import { h } from 'preact'
import { useCallback } from 'preact/hooks'
import PropTypes from 'prop-types'

import classNames from 'lib/classNames'
import Icon from 'components/Icon'
import EndUserAvatar from 'components/EndUserAvatar'

export default function ParticipantListPanel({
  participants,
  localDisplayName,
  isMicMuted,
  isVideoEnabled,
  isScreenSharing,
  onClose,
  isAdmin,
  onKickParticipant,
  onMuteParticipant,
}) {
  const handleKick = useCallback((identity) => {
    if (onKickParticipant) onKickParticipant(identity)
  }, [onKickParticipant])

  const handleMute = useCallback((identity) => {
    if (onMuteParticipant) onMuteParticipant(identity)
  }, [onMuteParticipant])

  return <div className="ParticipantListPanel">
    <div className="ParticipantListPanel-header">
      <span className="ParticipantListPanel-title">
        Participants ({1 + participants.length})
      </span>
      <button
        className="ParticipantListPanel-closeBtn"
        onClick={onClose}
        title="Close"
      >
        <Icon type="close" size="sm" />
      </button>
    </div>

    <div className="ParticipantListPanel-list">
      {/* Local user */}
      <div className="ParticipantListPanel-item">
        <EndUserAvatar
          size="sm"
          publicProfile={{ username: localDisplayName || 'You' }}
          className="ParticipantListPanel-avatar"
        />
        <div className="ParticipantListPanel-info">
          <span className="ParticipantListPanel-name">
            {localDisplayName || 'You'} (You)
          </span>
          {isAdmin && <span className="ParticipantListPanel-role">Host</span>}
        </div>
        <div className="ParticipantListPanel-status">
          <span className={classNames('ParticipantListPanel-statusIcon', { off: isMicMuted })}
            title={isMicMuted ? 'Muted' : 'Mic on'}>
            <Icon type={isMicMuted ? 'audioOff' : 'audio'} size="sm" />
          </span>
          <span className={classNames('ParticipantListPanel-statusIcon', { off: !isVideoEnabled })}
            title={isVideoEnabled ? 'Camera on' : 'Camera off'}>
            <Icon type={isVideoEnabled ? 'video' : 'disabledVideo'} size="sm" />
          </span>
          {isScreenSharing &&
            <span className="ParticipantListPanel-statusIcon ParticipantListPanel-statusIcon-screen"
              title="Sharing screen">
              <Icon type="shareScreen" size="sm" />
            </span>
          }
        </div>
      </div>

      {/* Remote participants */}
      {participants.map(p =>
        <div key={p.identity} className="ParticipantListPanel-item">
          <EndUserAvatar
            size="sm"
            publicProfile={{ username: p.name || p.identity }}
            className="ParticipantListPanel-avatar"
          />
          <div className="ParticipantListPanel-info">
            <span className="ParticipantListPanel-name">
              {p.name || p.identity}
              {p.isVirtual && <span className="ParticipantListPanel-virtual"> (Virtual)</span>}
            </span>
            {p.isSpeaking &&
              <span className="ParticipantListPanel-speaking">Speaking</span>
            }
          </div>
          <div className="ParticipantListPanel-status">
            <span className={classNames('ParticipantListPanel-statusIcon', { off: p.isMuted })}
              title={p.isMuted ? 'Muted' : 'Mic on'}>
              <Icon type={p.isMuted ? 'audioOff' : 'audio'} size="sm" />
            </span>
            <span className={classNames('ParticipantListPanel-statusIcon', { off: !p.isVideoEnabled })}
              title={p.isVideoEnabled ? 'Camera on' : 'Camera off'}>
              <Icon type={p.isVideoEnabled ? 'video' : 'disabledVideo'} size="sm" />
            </span>
            {p.isScreenSharing &&
              <span className="ParticipantListPanel-statusIcon ParticipantListPanel-statusIcon-screen"
                title="Sharing screen">
                <Icon type="shareScreen" size="sm" />
              </span>
            }
            {/* Admin actions */}
            {isAdmin && (
              <div className="ParticipantListPanel-adminActions">
                {!p.isMuted && (
                  <button
                    className="ParticipantListPanel-adminBtn"
                    onClick={() => handleMute(p.identity)}
                    title="Mute participant"
                  >
                    <Icon type="audioOff" size="sm" />
                  </button>
                )}
                <button
                  className="ParticipantListPanel-adminBtn ParticipantListPanel-adminBtn-danger"
                  onClick={() => handleKick(p.identity)}
                  title="Remove from call"
                >
                  <Icon type="close" size="sm" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
}

ParticipantListPanel.propTypes = {
  participants: PropTypes.array.isRequired,
  localIdentity: PropTypes.string,
  localDisplayName: PropTypes.string,
  isMicMuted: PropTypes.bool,
  isVideoEnabled: PropTypes.bool,
  isScreenSharing: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  onKickParticipant: PropTypes.func,
  onMuteParticipant: PropTypes.func,
}
